import { useState, useEffect } from 'react'
import { Lock, AlertCircle, Mail, KeyRound, Eye, EyeOff } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/shared/contexts/AuthContext'
import { Header } from '@/shared/components/layout/Header'
import { StatusBar } from '@/shared/components/layout/StatusBar'
import { INVITE_ONLY } from '@/shared/lib/inviteOnly'

type AuthMode = 'login' | 'register' | 'forgot'

export function LoginForm() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [mode, setMode] = useState<AuthMode>('login')
  const [showPassword, setShowPassword] = useState(false)
  const { user, signIn, signUp, resetPassword } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const redirectTo = searchParams.get('redirect') || '/dashboard'

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate(redirectTo, { replace: true })
    }
  }, [user, navigate, redirectTo])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setIsLoading(true)

    try {
      if (mode === 'login') {
        const { error } = await signIn(email, password)
        if (error) {
          setError(translateAuthError(error.message))
        }
      } else if (mode === 'register') {
        const { error } = await signUp(email, password)
        if (error) {
          setError(translateAuthError(error.message))
        } else {
          setSuccess('Cuenta creada. Te enviamos un email para confirmar tu cuenta.')
          setMode('login')
          setPassword('')
        }
      } else if (mode === 'forgot') {
        const { error } = await resetPassword(email)
        if (error) {
          setError(translateAuthError(error.message))
        } else {
          setSuccess('Enviamos un email con instrucciones para restablecer tu contraseña.')
        }
      }
    } catch {
      setError('Error inesperado. Por favor, intenta nuevamente.')
    } finally {
      setIsLoading(false)
    }
  }

  const switchMode = (next: AuthMode) => {
    setMode(next)
    setError('')
    setSuccess('')
    if (next === 'forgot') setPassword('')
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-legal-50 to-legal-100 dark:from-legal-900 dark:to-legal-800 pb-7">
      <Header hideAuthControls />
      <div className="flex-1 flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full">
        {/* Auth Card */}
        <div className="bg-white dark:bg-legal-800 rounded-2xl shadow-xl p-8">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6 text-center">
            {mode === 'login' && 'Iniciar Sesión'}
            {mode === 'register' && 'Crear Cuenta'}
            {mode === 'forgot' && 'Recuperar Contraseña'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Input */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 dark:border-legal-600 bg-white dark:bg-legal-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-legal-500 focus:border-transparent transition"
                  placeholder="tu@email.com"
                  required
                  autoFocus
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Password Input - hidden in forgot mode */}
            {mode !== 'forgot' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Contraseña
                  </label>
                  {mode === 'login' && (
                    <button
                      type="button"
                      onClick={() => switchMode('forgot')}
                      className="text-xs text-legal-600 dark:text-legal-400 hover:underline"
                    >
                      ¿Olvidaste tu contraseña?
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 rounded-lg border border-gray-300 dark:border-legal-600 bg-white dark:bg-legal-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-legal-500 focus:border-transparent transition"
                    placeholder={mode === 'register' ? 'Mínimo 6 caracteres' : 'Tu contraseña'}
                    required
                    minLength={mode === 'register' ? 6 : undefined}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-300 text-sm">
                <AlertCircle size={18} className="flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="flex items-center gap-2 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg text-green-800 dark:text-green-300 text-sm">
                <span>{success}</span>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || !email || (mode !== 'forgot' && !password)}
              className="w-full btn-primary py-3 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {mode === 'forgot' && !isLoading && <KeyRound size={18} />}
              {isLoading
                ? 'Procesando...'
                : mode === 'login'
                  ? 'Ingresar'
                  : mode === 'register'
                    ? 'Crear Cuenta'
                    : 'Enviar email de recuperacion'}
            </button>
          </form>

          {/* Toggle Mode */}
          <div className="mt-6 text-center space-y-2">
            {mode === 'login' && INVITE_ONLY && (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Acceso solo por invitación
              </p>
            )}
            {mode === 'login' && !INVITE_ONLY && (
              <button
                type="button"
                onClick={() => switchMode('register')}
                className="text-sm text-legal-600 dark:text-legal-400 hover:text-legal-800 dark:hover:text-legal-200 underline"
              >
                No tenes cuenta? Crear una
              </button>
            )}
            {mode === 'register' && (
              <button
                type="button"
                onClick={() => switchMode('login')}
                className="text-sm text-legal-600 dark:text-legal-400 hover:text-legal-800 dark:hover:text-legal-200 underline"
              >
                Ya tenes cuenta? Iniciar sesion
              </button>
            )}
            {mode === 'forgot' && (
              <button
                type="button"
                onClick={() => switchMode('login')}
                className="text-sm text-legal-600 dark:text-legal-400 hover:text-legal-800 dark:hover:text-legal-200 underline"
              >
                Volver a iniciar sesión
              </button>
            )}
          </div>

          {/* Info */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-legal-700">
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
              Plataforma para verificación de firmas digitales segun{' '}
              <span className="font-semibold">Ley 25.506</span>
            </p>
          </div>
        </div>

      </div>
      </div>
      <StatusBar />
    </div>
  )
}

/**
 * Translate common Supabase auth errors to Spanish
 */
function translateAuthError(message: string): string {
  if (message.includes('Invalid login credentials')) {
    return 'Email o contraseña incorrectos.'
  }
  if (message.includes('Email not confirmed')) {
    return 'Email no confirmado. Revisa tu bandeja de entrada.'
  }
  if (message.includes('User already registered')) {
    return 'Este email ya esta registrado. Intenta iniciar sesión.'
  }
  if (message.includes('Password should be at least')) {
    return 'La contraseña debe tener al menos 6 caracteres.'
  }
  if (message.includes('rate limit') || message.includes('too many requests')) {
    return 'Demasiados intentos. Espera un momento e intenta nuevamente.'
  }
  return message
}
